import { unref } from '@vue/composition-api'
import membersApi from '@/services/api/members'
import useServerList from '@/composables/useServerList'

/**
 * Members list — /members (Secretary) and /admin-members (Admin/SuperAdmin) both use this, as
 * does the Horse Profile "Members" tab (CAM-1504, mirrors the Member Profile "Horses" tab —
 * CAM-1505 — in the opposite direction). Unlike Horses/Drafts there is no per-role dataset scope
 * here (the page was never member-family scoped — 'secretary' middleware just covers Secretary
 * through SuperAdmin); the only per-role difference (the Login column/action) is UI-only and
 * handled in the view via `$isAdmin`.
 *
 * @param {number|import('@vue/composition-api').Ref<number>|null} horseId  the Horse Profile
 *   "Members" tab passes the profile's horse id so the list is scoped to that horse's
 *   owners/riders and their whole account family instead of every member. Backend requires no
 *   extra gate for this param — GET /v2/members is already secretary-gated for every caller.
 */
export default function useMembersList(horseId = null) {
  const getList = (params) => {
    const hid = unref(horseId)
    return membersApi.getList(hid ? { ...params, horse_id: hid } : params)
  }

  return useServerList(getList, {
    defaultSort: 'last_name',
    defaultDirection: 'asc',
    defaultPerPage: 20,
    extraFilters: {
      // 'all' | '1' (Financial) | '0' (Unfinancial) | 'Volunteer'
      financial: 'all',
      // 'all' | Onhold | Archived | Lifetime | Suspended | Deleted
      membership_status: 'all',
      // Multi-value dropdown filters (CAM-1477 follow-up) — array default => CSV in the URL/request.
      zone_id: [],
      // Current rider_status_id, as opposed to boy_status_id below.
      status_id: [],
      boy_status_id: [],
      // '' (all) | '1' (primary accounts only, hides secondaries)
      primary_only: '',
      // '' (all) | '1' (admin only — role=Secretary, i.e. a secretary who's also a member)
      secretary_members_only: '',
      // 'owner' | 'rider' | 'family' — only meaningful together with `horseId` (Horse Profile
      // "Members" tab); a no-op filter for the plain /members and /admin-members pages.
      relation: []
    },
    stringMultiFilters: ['relation']
  })
}



// WEBPACK FOOTER //
// ./src/composables/members/useMembersList.js